import { useState, useEffect } from 'react';
import { Modal, ModalFooter } from '../../ui/Modal';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { Textarea } from '../../ui/Textarea';
import { Button } from '../../ui/Button';
import { LONG_TERM_POSITIONS } from '../../../types/meetings';
import type { ServicePosition, SavedMeeting } from '../../../types/meetings';

interface ServicePositionFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Omit<ServicePosition, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  initialData?: ServicePosition;
  savedMeetings: SavedMeeting[];
}

type FormState = {
  positionName: string;
  customPosition: string;
  meetingId: string;
  meetingName: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  notes: string;
};

const today = new Date().toISOString().split('T')[0];

export function ServicePositionForm({
  isOpen,
  onClose,
  onSave,
  initialData,
  savedMeetings,
}: ServicePositionFormProps) {
  const [form, setForm] = useState<FormState>({
    positionName: '',
    customPosition: '',
    meetingId: '',
    meetingName: '',
    startDate: today,
    endDate: '',
    isActive: true,
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ positionName?: string; startDate?: string }>({});

  useEffect(() => {
    if (!isOpen) return;
    if (initialData) {
      const isCustom = !LONG_TERM_POSITIONS.includes(initialData.positionName as typeof LONG_TERM_POSITIONS[number]);
      setForm({
        positionName: isCustom ? 'Other' : initialData.positionName,
        customPosition: isCustom ? initialData.positionName : '',
        meetingId: initialData.meetingId ?? '',
        meetingName: initialData.meetingName ?? '',
        startDate: initialData.startDate,
        endDate: initialData.endDate ?? '',
        isActive: initialData.isActive,
        notes: initialData.notes ?? '',
      });
    } else {
      setForm({
        positionName: '',
        customPosition: '',
        meetingId: '',
        meetingName: '',
        startDate: today,
        endDate: '',
        isActive: true,
        notes: '',
      });
    }
    setErrors({});
  }, [isOpen, initialData]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  }

  function handleMeetingSelect(meetingId: string) {
    const meeting = savedMeetings.find(m => m.id === meetingId);
    setForm(prev => ({
      ...prev,
      meetingId,
      meetingName: meeting?.name ?? '',
    }));
  }

  function validate(): boolean {
    const newErrors: typeof errors = {};
    const resolvedPosition = form.positionName === 'Other' ? form.customPosition.trim() : form.positionName;
    if (!resolvedPosition) newErrors.positionName = 'Position name is required';
    if (!form.startDate) newErrors.startDate = 'Start date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;
    setSaving(true);
    try {
      const resolvedPosition = form.positionName === 'Other'
        ? form.customPosition.trim()
        : form.positionName;
      await onSave({
        positionName: resolvedPosition,
        ...(form.meetingId ? { meetingId: form.meetingId, meetingName: form.meetingName } : {}),
        startDate: form.startDate,
        ...(form.endDate ? { endDate: form.endDate } : {}),
        isActive: form.isActive,
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const meetingOptions = savedMeetings.map(m => ({ value: m.id, label: m.name }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Edit Service Position' : 'Add Service Position'}
      size="sm"
      closeOnOverlayClick={false}
    >
      <div className="space-y-4">
        {/* Position name */}
        <Select
          label="Position"
          value={form.positionName}
          onChange={e => set('positionName', e.target.value)}
          options={LONG_TERM_POSITIONS.map(p => ({ value: p, label: p }))}
          placeholder="Select position…"
          error={errors.positionName}
        />

        {form.positionName === 'Other' && (
          <Input
            label="Custom position title"
            placeholder="Describe the position…"
            value={form.customPosition}
            onChange={e => set('customPosition', e.target.value)}
            error={errors.positionName && !form.customPosition ? errors.positionName : undefined}
          />
        )}

        {/* Meeting (optional) */}
        {savedMeetings.length > 0 && (
          <Select
            label="Meeting (optional)"
            value={form.meetingId}
            onChange={e => handleMeetingSelect(e.target.value)}
            options={meetingOptions}
            placeholder="Not linked to a meeting"
          />
        )}

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Start date"
            type="date"
            value={form.startDate}
            onChange={e => set('startDate', e.target.value)}
            max={today}
            error={errors.startDate}
          />
          <Input
            label="End date (optional)"
            type="date"
            value={form.endDate}
            onChange={e => set('endDate', e.target.value)}
            min={form.startDate}
          />
        </div>

        {/* Active toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-surface-900 dark:text-surface-100">Currently active</p>
            <p className="text-xs text-surface-500 dark:text-surface-400">Toggle off when position ends</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={e => set('isActive', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-surface-300 rounded-full peer dark:bg-surface-700 peer-checked:bg-teal-600 dark:peer-checked:bg-teal-400 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
          </label>
        </div>

        {/* Notes */}
        <Textarea
          label="Notes (optional)"
          placeholder="Rotation, responsibilities…"
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          rows={3}
        />
      </div>

      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} isLoading={saving}>
          {initialData ? 'Save Changes' : 'Add Position'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
